import { createRouter, createWebHistory, RouteRecordRaw, RouteComponent } from "vue-router";
import Login from "./views/Login.vue";
import { Role } from "@core/domain/types/role.type";
import ProjectsVue from "./views/Projects/Projects.vue";
import { Square3Stack3DIcon } from "@heroicons/vue/24/outline";
import UsersVue from "./views/Users.vue";
import UsersIconVue from "./components/Icons/UsersIcon.vue";

import Settings from "./views/Settings/Settings.vue";
import SettingsIcon from "./components/Icons/SettingsIcon.vue";
import { User } from "@core/domain/models/user.model";
import ForgotPassword from "./views/ForgotPassword.vue";
import Error404 from "./views/Error/Error404.vue";
import Plan from "./views/Review/Plan/Plan.vue";
import Evaluators from "./views/Review/Plan/Evaluators/Evaluators.vue";
import Objective from "./views/Review/Plan/Objective/Objective.vue";
import PlanSynthesis from "./views/Review/Plan/Synthesis/Synthesis.vue";
import Strategy from "./views/Review/Plan/Strategy/Strategy.vue";
import PlanAppraisal from "./views/Review/Plan/Appraisal/Appraisal.vue";
import PlanImportSources from "./views/Review/Plan/Search/Searches.vue";
import Screening from "./views/Review/Execute/Screening/Screening.vue";
import Execute from "./views/Review/Execute/Execute.vue";
import DataExtraction from "./views/Review/Execute/DataExtraction/DataExtraction.vue";
import Appraisal from "./views/Review/Execute/Appraisal/Appraisal.vue";
import SynthesisView from "./views/Review/Execute/Synthesis/SynthesisView.vue";
import Review from "./views/Review/Review.vue";
import AsyncReview from "./views/Review/AsyncReview.vue";

export enum RouteName {
  PROJECTS = "Projects",
  USERS = "Users",
  SETTINGS = "App Settings",
  LOGIN = "login",
  FORGOT_PASSWORD = "forgotPassword",
  ERROR_404 = "404 not found",
  TERMS_OF_SERVICE = "termsOfService",
  REVIEW = "Review",
  REVIEW_PLAN = "Review - Plan",
  REVIEW_PLAN_EVALUATORS = "Review - Plan - Evaluators",
  REVIEW_PLAN_OBJECTIVE = "Review - Plan - Objective",
  REVIEW_PLAN_SYNTHESIS = "Review - Plan - Synthesis",
  REVIEW_PLAN_STRATEGY = "Review - Plan - Strategy",
  REVIEW_PLAN_APPRAISAL = "Review - Plan - Appraisal",
  REVIEW_PLAN_SEARCH = "Review - Plan - Search",
  REVIEW_EXECUTE = "Review - Execute",
  REVIEW_EXECUTE_SCREENING = "Review - Execute - Screening",
  REVIEW_EXECUTE_DATA_EXTRACTION = "Review - Execute - Data Extraction",
  REVIEW_EXECUTE_SYNTHESIS = "Review - Execute - Synthesis",
  REVIEW_EXECUTE_SYNTHESIS_DELTA = "Review - Execute - Synthesis Delta",
  REVIEW_EXECUTE_APPRAISAL = "Review - Execute - Appraisal",
  REVIEW_EXPORT = "Review - Export",
  REVIEW_ITEM = "Review - Review Item",
}

export interface NagivationEntry {
  to: string;
  roles: Role[];
  name: RouteName;
  component: RouteComponent;
  icon: RouteComponent;
  text: string;
  isCuttingEdge?: boolean;
}
export const navigation: NagivationEntry[] = [
  {
    to: "/projects",
    component: ProjectsVue,
    roles: [],
    name: RouteName.PROJECTS,
    icon: Square3Stack3DIcon,
    text: "Projects",
  },
  {
    to: "/users",
    component: UsersVue,
    roles: [Role.ADMIN],
    name: RouteName.USERS,
    icon: UsersIconVue,
    text: "Users",
  },
  {
    to: "/settings",
    component: Settings,
    roles: [],
    name: RouteName.SETTINGS,
    icon: SettingsIcon,
    text: "Settings",
  },
];

export function userCanAccess(routeName: string, user: User): boolean {
  const route = navigation.find((r) => r.name === routeName);
  if (!route) throw new Error("Incorrect route name");
  if (route.roles.length <= 0) return true;
  if (!user) return false;
  return route.roles.includes(user.role);
}

export function createAppRouter() {
  const routes: RouteRecordRaw[] = [
    {
      path: "/",
      redirect: {
        replace: true,
        name: RouteName.PROJECTS,
      },
    },
    {
      path: "/reviews/:id",
      component: Review,
      children: [
        {
          path: "",
          component: AsyncReview,
          name: RouteName.REVIEW,

          children: [
            {
              path: "plan",
              component: Plan,
              name: RouteName.REVIEW_PLAN,
              redirect: {
                name: RouteName.REVIEW_PLAN_EVALUATORS,
                replace: true,
              },
              children: [
                {
                  path: "evaluators",
                  component: Evaluators,
                  name: RouteName.REVIEW_PLAN_EVALUATORS,
                },
                {
                  path: "objective",
                  component: Objective,
                  name: RouteName.REVIEW_PLAN_OBJECTIVE,
                },
                {
                  path: "synthesis",
                  component: PlanSynthesis,
                  name: RouteName.REVIEW_PLAN_SYNTHESIS,
                },
                {
                  path: "strategy",
                  component: Strategy,
                  name: RouteName.REVIEW_PLAN_STRATEGY,
                },
                {
                  path: "appraisal",
                  component: PlanAppraisal,
                  name: RouteName.REVIEW_PLAN_APPRAISAL,
                },
                {
                  path: "search",
                  component: PlanImportSources,
                  name: RouteName.REVIEW_PLAN_SEARCH,
                },
              ],
            },
            {
              path: "execute",
              component: Execute,
              redirect: (route) => ({
                name: RouteName.REVIEW_EXECUTE_SCREENING,
                params: route.params,
                replace: true,
              }),
              name: RouteName.REVIEW_EXECUTE,
              children: [
                {
                  path: "screening",
                  component: Screening,
                  name: RouteName.REVIEW_EXECUTE_SCREENING,
                },
                {
                  path: "data-extraction",
                  component: DataExtraction,
                  name: RouteName.REVIEW_EXECUTE_DATA_EXTRACTION,
                },
                {
                  path: "synthesis",
                  component: SynthesisView,
                  name: RouteName.REVIEW_EXECUTE_SYNTHESIS,
                },
                {
                  path: "synthesis-delta",
                  component: () =>
                    import("./views/Review/Execute/SynthesisDelta/SynthesisDeltaView.vue"),
                  name: RouteName.REVIEW_EXECUTE_SYNTHESIS_DELTA,
                },
                {
                  path: "appraisal",
                  component: Appraisal,
                  name: RouteName.REVIEW_EXECUTE_APPRAISAL,
                },
              ],
            },
            {
              path: "export",
              component: () => import("./views/Review/Export/Export.vue"),
              name: RouteName.REVIEW_EXPORT,
            },
          ],
        },
        {
          path: "review-item/:reviewItemId",
          component: () => import("./views/Review/ReviewItemFullView/ReviewItemFullView.vue"),
          name: RouteName.REVIEW_ITEM,
        },
      ],
    },
    { path: "/login", component: Login, name: RouteName.LOGIN },
    { path: "/404", component: Error404, name: RouteName.ERROR_404 },
    {
      path: "/forgot-password",
      component: ForgotPassword,
      name: RouteName.FORGOT_PASSWORD,
    },

    {
      path: "/terms-of-service",
      component: () => import("./views/TermsOfService.vue"),
      name: RouteName.TERMS_OF_SERVICE,
    },

    ...navigation.map((n) => ({
      path: n.to,
      component: n.component,
      name: n.name,
    })),
  ];
  return createRouter({
    history: createWebHistory(),
    routes,
  });
}
